import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { Control } from "react-hook-form/dist/types/form";
import {
  StyledLabel,
  TextFieldWrapper,
  WarnLabel,
} from "../TextFieldController";
import { ClickAwayListener } from "@mui/material";
import {
  LabelValuePair,
  SubLabeledOption,
} from "../staticSelect/LabelValuePair";
import styled, { useTheme } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import TooltipNew from "../../common/TooltipNew";
import {
  faCaretDown,
  faCaretUp,
  faCheckCircle,
  faExclamation,
  faExclamationCircle,
  faFrown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CoreRequest,
  PayrollRequest,
} from "../../../redux/config/serviceInstances";
import Loader, { LoaderSize } from "../../loader/Loader";
import { TextVariant, TypographyNew } from "../../typography/typography";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { EmployeeAvatarDto } from "../../../redux/api-model/organization/company";
import { WorkContractStatus } from "../../../redux/api-model/employees/workContract";
import EmployeeAvatar from "../../avatars/EmployeeAvatar";
import { debounce } from "../../../utils/debounce";
import { MissingDataFilter } from "../../../redux/api-model/employees/employee";
import LinkStyled from "../../buttonsLinks/LinkStyled";
import { backDrop, flapOut, openButton } from "../../../utils/z-index";
import useSelectColors from "../useSelectColors";

export const MainContainer = styled.div`
  position: relative;
  height: 76px;
`;

export const MenuContainer = styled.div<{
  responsive?: boolean;
  open?: boolean;
  mobileResponsive?: boolean;
  backgroundColor: string;
  boxShadow: string;
  hoverBG: string;
  hoverBo: string;
  long?: boolean;
}>`
  position: relative;
  width: ${(props) => {
    if (props.mobileResponsive) {
      return "100%";
    }

    return props.long ? "500px" : "calc(100% - 30px)";
  }};
  min-height: 40px;
  max-height: 260px;
  overflow: auto;
  border: 1px solid ${(props) => props.theme.colors.grays4};
  background-color: ${(props) => props.backgroundColor};
  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.theme.borderRadius.small};
  font-family: ${(props) => props.theme.fonts.normal};
  font-size: 15px;
  font-weight: 400;
  z-index: ${(props) => (props.open ? flapOut : openButton)};

  @media (min-width: 600px) {
    width: ${(props) => (props.responsive ? "100%" : "304px")};
  }
  &:hover {
    background-color: ${(props) => props.hoverBG};
    border: ${(props) => props.hoverBo};
  }
`;

export const HintContainer = styled.div`
  position: relative;
  padding: 0px 10px 15px 10px;
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 400;
`;

export const LabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const MenuItemsContainer = styled.div<{ load?: boolean }>`
  position: relative;
  z-index: ${flapOut};
  background: ${(props) => props.theme.colors.white};
  min-height: ${({ load }) => (load ? "70px" : "auto")};
`;

export const SelectedValueContainer = styled.div<{
  color: string;
  open: boolean;
}>`
  position: sticky;
  top: 0;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.color};
  min-height: 40px;
  padding: 0px 10px 0px 10px;
  color: ${(props) => props.theme.colors.grays1};
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  z-index: ${(props) => (props.open ? backDrop : openButton)};
`;

export const StyledInput = styled.input<{
  error: boolean;
  $isCursorPointer?: boolean;
}>`
  width: 100%;
  height: 100%;
  border: none;
  cursor: ${(props) => (props.$isCursorPointer ? "pointer" : null)};
  font-family: ${(props) => props.theme.fonts.normal};
  font-weight: 400;
  font-size: 15px;
  outline: none;
  background-color: transparent;
`;

export const MenuItem = styled.div<{
  selected?: boolean;
  subLabeled?: boolean;
}>`
  position: relative;
  min-height: 40px;
  padding: ${(props) => (props.subLabeled ? "7px 10px" : "0px 10px 0px 10px")};
  color: ${(props) =>
    props.selected ? props.theme.colors.primary1 : props.theme.colors.grays1};
  display: ${(props) => !props.subLabeled && "flex"};
  align-items: center;
  background-color: ${(props) =>
    props.selected ? props.theme.colors.misc3 : props.theme.colors.white};
  &:hover {
    background-color: ${(props) => props.theme.colors.misc3};
    color: ${(props) => props.theme.colors.primary1};
    cursor: pointer;
  }
`;

const StyledAvatar = styled(EmployeeAvatar)`
  cursor: pointer;

  a {
    pointer-events: none;
  }
`;

type HintProps = {
  minSize: boolean;
  minLength: number;
  loading: boolean;
  searchEmpl: boolean;
  error?: boolean;
};

const CreateEmployeeLink = styled(LinkStyled)`
  font-size: 14px;
`;

export const Hint = ({
  minSize,
  minLength,
  loading,
  searchEmpl,
  error,
}: HintProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation("general");

  if (loading || error) {
    return <></>;
  }

  return minSize ? (
    <HintContainer>{t("search.hint", { minLength })}</HintContainer>
  ) : (
    <>
      <HintContainer>
        <TypographyNew color={"darkgrey"} variant={TextVariant.body2}>
          {searchEmpl ? (
            <>
              {
                "Keine Ergebnisse (Hinweis: Nur vollständige Mitarbeiter werden berücksichtigt.)"
              }
              <br />
              <CreateEmployeeLink to={"/app/employees/add"}>
                {"Neuen Mitarbeiter anlegen"}
              </CreateEmployeeLink>
            </>
          ) : (
            "Keine Ergebnisse"
          )}
        </TypographyNew>
        <FontAwesomeIcon
          icon={faFrown as IconProp}
          color={theme.colors.primary1}
        />
      </HintContainer>
    </>
  );
};

const ErrorMessageContainer = styled.div`
  display: flex;
  padding-left: 10px;
  padding-bottom: 15px;
`;

const OptionLabel = styled.span`
  display: block;
`;

const SubOptionLabel = styled(TypographyNew)`
  display: block;
  margin-top: 5px;
  color: ${(props) => props.theme.colors.grays3};
`;

const ErrorMessage = (): JSX.Element => {
  const theme = useTheme();
  return (
    <ErrorMessageContainer>
      <TypographyNew variant={TextVariant.body2} color={theme.colors.accent2}>
        Auswahl konnte nicht geladen werden
      </TypographyNew>
      <FontAwesomeIcon icon={faExclamation as IconProp} color={"red"} />
    </ErrorMessageContainer>
  );
};

type SelectNewTextFieldProps<T> = {
  responsive?: boolean;
  mobileResponsive?: boolean;
  control: Control<any>;
  fieldName: `${string}`;
  defaultValue?: number | null | string;
  placeholder: string;
  label?: string;
  onChange?: (employee?: EmployeeAvatarDto) => void;
  preloadedList?: EmployeeAvatarDto[];
  initialLoad?: boolean;
  onTransformResponseToPairs?: (employee?: T) => LabelValuePair[];
  getQueryParams?: (
    searchTerm?: string
  ) => Record<string, string | number | boolean | undefined>;
  labelsEndpoint: string;
  selectionMode?: boolean;
  payrollX?: boolean;
  tooltip?: string;
  rules?: { required?: boolean; minLength?: number };
  errorMessage?: string;
  long?: boolean;
  className?: string;
  minLengthToCall?: number;
  workContractSelect?: {
    contractStatus?: WorkContractStatus;
    missingEmployeeDataFilter?: MissingDataFilter | null;
    companyId?: string;
  };
};

type ResponseOptions = {
  avatars?: EmployeeAvatarDto[];
  labelValue?: LabelValuePair[];
};

export default function SelectNewTextField<T>(
  props: SelectNewTextFieldProps<T>
): React.ReactElement {
  const theme = useTheme();
  const isSelectionOnlyMode = Boolean(props.selectionMode);
  const [initialLoaded, setInitialyLoaded] = useState<boolean>(false);
  const [state, setState] = useState<{
    open: boolean;
    filled: boolean;
    error: boolean;
    loading: boolean;
  }>({
    open: false,
    filled: false,
    error: false,
    loading: false,
  });

  const { backgroundColor, color, boxShadow, hoverBG, hoverBo } =
    useSelectColors(state.open, state.filled);

  const minLength = props.minLengthToCall ?? 3;
  return (
    <MainContainer className={props.className}>
      <Controller
        name={props.fieldName}
        control={props.control}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({
          field: { onChange, value },
          fieldState: { error, isDirty },
        }) => {
          const [menuItemsStorage, setMenuItemsStorage] =
            useState<ResponseOptions>({ avatars: props.preloadedList });

          const search = (s: string) => {
            const instance = props.payrollX ? PayrollRequest : CoreRequest;
            if (!props.workContractSelect) {
              return instance
                .get(props.labelsEndpoint, {
                  params: props.getQueryParams?.(s) ?? { searchTerm: s },
                })
                .then((response) => {
                  const transformedData = props.onTransformResponseToPairs?.(
                    response.data
                  );

                  if (transformedData) {
                    return {
                      ...response,
                      data: transformedData,
                    };
                  }

                  return response;
                })
                .then((r) => {
                  setMenuItemsStorage({ labelValue: r.data });
                  setState({
                    ...state,
                    open: true,
                    loading: false,
                  });
                })
                .catch(() => {
                  setState({
                    ...state,
                    open: true,
                    error: true,
                    loading: false,
                  });
                });
            } else {
              const missingDataFilter = props.workContractSelect
                .missingEmployeeDataFilter
                ? props.workContractSelect.missingEmployeeDataFilter
                : MissingDataFilter.ANY;
              const params =
                props.workContractSelect.companyId !== undefined
                  ? {
                      phrase: s,
                      contractStatus:
                        props.workContractSelect.contractStatus ??
                        WorkContractStatus.ACTIVE,
                      missingEmployeeDataFilter: missingDataFilter,
                      company: props.workContractSelect.companyId,
                    }
                  : {
                      phrase: s,
                      missingEmployeeDataFilter: missingDataFilter,
                      contractStatus: props.workContractSelect.contractStatus,
                    };
              return instance
                .get(`/contracts/labels`, {
                  params: params,
                })
                .then((r) => {
                  setMenuItemsStorage({ avatars: r.data });
                  setState({
                    ...state,
                    open: true,
                    loading: false,
                  });
                })
                .catch(() => {
                  setState({
                    ...state,
                    open: true,
                    error: true,
                    loading: false,
                  });
                });
            }
          };

          const debouncedSearch = useCallback(debounce(search, 300), [
            props.workContractSelect,
          ]);

          let filteredMenuItems: any = [];

          if (menuItemsStorage?.labelValue !== undefined) {
            filteredMenuItems = menuItemsStorage.labelValue.filter(
              (item: LabelValuePair) => item.value == value
            );
          } else if (menuItemsStorage?.avatars !== undefined) {
            filteredMenuItems = menuItemsStorage.avatars.filter(
              (item: EmployeeAvatarDto) => item.contractId == value
            );
          }

          const [searchValue, setSearchValue] = useState("");
          const minSize = searchValue.length <= minLength;
          const loadItems = (s: string) => {
            if (isSelectionOnlyMode) {
              return;
            }

            if (s.length > minLength - 1) {
              setState({
                ...state,
                open: true,
                loading: true,
              });
              debouncedSearch(s);
              onChange("");
            }
            if (s.length <= minLength) {
              setMenuItemsStorage({ labelValue: [], avatars: [] });
              onChange("");
            }
            setSearchValue(s);
          };
          let inputValue;
          if (filteredMenuItems[0] !== undefined) {
            if (props.workContractSelect || isSelectionOnlyMode) {
              inputValue = `${filteredMenuItems[0].firstName} ${filteredMenuItems[0].lastName}`;
            } else {
              inputValue = filteredMenuItems[0].label;
            }
          } else {
            inputValue = searchValue;
          }

          function onFocusHandler() {
            if (props.initialLoad && !initialLoaded) {
              setState((prevState) => ({
                ...prevState,
                open: true,
                loading: true,
              }));
              search("").then(() => setInitialyLoaded(true));
            }
          }

          return (
            <TextFieldWrapper responsive={props.responsive}>
              {props.label && (
                <StyledLabel>
                  <LabelContainer>
                    <label> {props.label}</label>&nbsp;
                    {props.tooltip ? (
                      <TooltipNew message={props.tooltip} />
                    ) : (
                      <></>
                    )}
                    {filteredMenuItems[0] !== undefined ? (
                      <>
                        &nbsp;
                        <FontAwesomeIcon
                          icon={faCheckCircle as IconProp}
                          color={theme.colors.accent7}
                        />
                      </>
                    ) : (
                      <></>
                    )}
                    {(filteredMenuItems[0] == undefined && isDirty) ||
                    error !== undefined ? (
                      <>
                        &nbsp;
                        <FontAwesomeIcon
                          icon={faExclamationCircle as IconProp}
                          color={theme.colors.accent1}
                        />
                      </>
                    ) : (
                      <></>
                    )}
                  </LabelContainer>
                </StyledLabel>
              )}
              <MenuContainer
                open={state.open}
                mobileResponsive={props.mobileResponsive}
                responsive={props.responsive}
                backgroundColor={backgroundColor}
                boxShadow={boxShadow}
                color={color}
                hoverBG={hoverBG}
                hoverBo={hoverBo}
                long={props.long}
                onClick={() => {
                  setState({ ...state, open: !state.open });
                }}
              >
                <SelectedValueContainer color={color} open={state.open}>
                  <StyledInput
                    $isCursorPointer={isSelectionOnlyMode}
                    placeholder={props.placeholder}
                    readOnly={isSelectionOnlyMode}
                    onFocus={onFocusHandler}
                    error={error !== undefined}
                    onChange={(e) => loadItems(e.target.value)}
                    value={inputValue}
                    type={"text"}
                  />
                  <FontAwesomeIcon
                    icon={
                      state.open
                        ? (faCaretUp as IconProp)
                        : (faCaretDown as IconProp)
                    }
                    color={theme.colors.grays1}
                  />
                </SelectedValueContainer>
                {state.open ? (
                  <ClickAwayListener
                    onClickAway={() => setState({ ...state, open: false })}
                  >
                    <MenuItemsContainer load={state.loading}>
                      {state.error && <ErrorMessage />}
                      {menuItemsStorage?.avatars !== undefined ? (
                        menuItemsStorage?.avatars.length == 0 ? (
                          <Hint
                            loading={state.loading}
                            error={state.error}
                            minSize={minSize}
                            minLength={minLength}
                            searchEmpl={props.workContractSelect != undefined}
                          />
                        ) : (
                          menuItemsStorage.avatars.map(
                            (item: EmployeeAvatarDto) => {
                              return (
                                <MenuItem
                                  onClick={() => {
                                    onChange(item.contractId);
                                    props.onChange?.(item);
                                  }}
                                  key={uuidv4()}
                                  selected={item.contractId === value}
                                  style={{ padding: 10 }}
                                >
                                  <StyledAvatar
                                    contractId={item.contractId}
                                    employeeId={item.uuid}
                                    firstName={item.firstName}
                                    lastName={item.lastName}
                                    missingData={item.missingData}
                                    position={item.position}
                                  />
                                </MenuItem>
                              );
                            }
                          )
                        )
                      ) : menuItemsStorage?.labelValue !== undefined ? (
                        menuItemsStorage?.labelValue.length == 0 ? (
                          <Hint
                            loading={state.loading}
                            minSize={minSize}
                            minLength={minLength}
                            searchEmpl={props.workContractSelect != undefined}
                          />
                        ) : (
                          menuItemsStorage?.labelValue.map(
                            (item: SubLabeledOption) => {
                              const isSubLabelAvailable = Boolean(
                                item.subLabel
                              );

                              return (
                                <MenuItem
                                  subLabeled={isSubLabelAvailable}
                                  onClick={() => {
                                    onChange(item.value);
                                  }}
                                  key={uuidv4()}
                                  selected={item.value === value}
                                >
                                  <OptionLabel title={item.label}>
                                    {item.label}
                                  </OptionLabel>
                                  {isSubLabelAvailable && (
                                    <SubOptionLabel
                                      variant={TextVariant.caption}
                                      as="span"
                                    >
                                      {item.subLabel}
                                    </SubOptionLabel>
                                  )}
                                </MenuItem>
                              );
                            }
                          )
                        )
                      ) : (
                        <></>
                      )}
                      {state.loading && <Loader size={LoaderSize.small} />}
                    </MenuItemsContainer>
                  </ClickAwayListener>
                ) : (
                  <></>
                )}
              </MenuContainer>
              {error && props.errorMessage ? (
                <WarnLabel>{props.errorMessage}</WarnLabel>
              ) : null}
            </TextFieldWrapper>
          );
        }}
      />
    </MainContainer>
  );
}
